/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

const IDEAL_DATA_ITEM_GAP = 2;
const DEFAULT_BAR_GAP_RATIO = 0.25;

/**
 * The function that calculates the barWidth and offset from group center for each series.
 * @param groupWidth The width of the group.
 * @param noOfSeries The number of series present in the chart.
 * @param barGapRatio The ratio of gap to groupWidth.
 * @param dataItemGaps The ratio that determines the gap between the bars.
 */
export function getBarGapsInfo(
  seriesIndex: number,
  groupWidth: number,
  barGapRatio: number,
  numOfSeries: number,
  dataItemGaps: number,
  isStacked: boolean,
  isRtl: boolean
) {
  const numSeries = isStacked ? 1 : numOfSeries;
  const itemsGaps = IDEAL_DATA_ITEM_GAP * dataItemGaps;
  const barWidth = (groupWidth * (1 - barGapRatio) - (numSeries - 1) * itemsGaps) / numSeries;
  if (isStacked) {
    return { barWidth, offset: (-1 * barWidth) / 2, itemsGaps };
  }
  const center = groupWidth / 2;
  const seriesStart =
    groupWidth * (barGapRatio / 2) + seriesIndex * (barWidth + IDEAL_DATA_ITEM_GAP * dataItemGaps);
  const offset = isRtl ? center - seriesStart - barWidth : seriesStart - center;

  return { barWidth, offset, itemsGaps };
}

/**
 * Returns the bar gap ratio.
 * @param isStacked Whether the chart is a stacked bar chart.
 * @param startIndex The viewport start index.
 * @param endIndex The viewport end index.
 */
export function getBarGapRatio(isStacked: boolean, startIndex: number, endIndex: number) {
  const noOfGroups = endIndex - startIndex + 1;
  // logic from legacy
  return isStacked ? 0.37 + 0.26 / noOfGroups : DEFAULT_BAR_GAP_RATIO;
}
